import { useState } from "react";
import { highlightBracketedText } from "src/utils/utils";
import { ImageAndTextListFragmentFragment } from "src/generated/graphql";
import Partner_Image_bg from "../../../images/Frame_partner.png";
import DynamicMedia from "src/components/shared/DynamicMedia";
import { useLocation } from "react-router-dom";

type AutomaticContentProps = {
  content: ImageAndTextListFragmentFragment;
  className?: string;
};

const PartnerImageAndList = ({ content, className }: AutomaticContentProps) => {
  const { imageAndTextListCollection } = content;
  const location = useLocation();
  const isPathSitecore = location.pathname === "/sitecore";
  
  const items = imageAndTextListCollection?.items || [];
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Video state management for current item
  const [videoState, setVideoState] = useState({
    isPlaying: false,
    showVideo: false,
  });

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
    // Reset video state when changing items
    setVideoState({ isPlaying: false, showVideo: false });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
    // Reset video state when changing items
    setVideoState({ isPlaying: false, showVideo: false });
  };

  const togglePlayPause = () => {
    const currentItem = items[currentIndex];
    const videoUrl = currentItem?.videofile?.url;
    const isVideo = videoUrl && (videoUrl.includes('.mp4') || videoUrl.includes('.webm') || videoUrl.includes('.mov'));
    
    if (!isVideo) return;

    if (!videoState.showVideo) {
      // First click - show video and start playing
      setVideoState({
        isPlaying: true,
        showVideo: true
      });
      
      // Small timeout to ensure video element is rendered before playing
      setTimeout(() => {
        const videoElement = document.querySelector('.partner-showcase__image-container video') as HTMLVideoElement;
        if (videoElement) {
          videoElement.play();
        }
      }, 10);
    } else {
      // Video is already showing - toggle play/pause
      const videoElement = document.querySelector('.partner-showcase__image-container video') as HTMLVideoElement;
      
      if (videoElement) {
        if (videoState.isPlaying) {
          videoElement.pause();
          setVideoState(prev => ({ ...prev, isPlaying: false }));
        } else {
          videoElement.play();
          setVideoState(prev => ({ ...prev, isPlaying: true }));
        }
      }
    }
  };

  const currentItem = items[currentIndex];
  const videoUrl = currentItem?.videofile?.url;
  const isVideo = videoUrl && (videoUrl.includes('.mp4') || videoUrl.includes('.webm') || videoUrl.includes('.mov'));
  
  // Use backgroundImage as thumbnail initially, then switch to video when playing
  const mediaSrc = isVideo && !videoState.showVideo 
    ? currentItem?.backgroundImage?.url 
    : (isVideo ? videoUrl : currentItem?.backgroundImage?.url);

  return (
    <div
      className={`partner-showcase ${className}`}
      style={{ paddingBottom: "250px", paddingTop: "85px" }}
    >
      <div
        className="partner-showcase__container"
        style={{ backgroundImage: `url(${Partner_Image_bg})` }}
      >
        <div className="partner-showcase__card">
          <div className="partner-showcase__content">
            <span className="partner-showcase__eyebrow">
              {currentItem?.title}
            </span>

            <div className="partner-showcase__description-nav-container">
              {(currentItem?.image?.url && isPathSitecore) && (
                <div className="partner-logo">
                  <img src={currentItem?.image?.url ?? ""} alt={currentItem?.image?.title ?? ""} />
                </div>
              )}
              <p className="partner-showcase__title">
                {
                  currentItem?.description?.json?.content?.[0]?.content?.[0]
                    ?.value
                }
              </p>
              {(currentItem?.eyebrowText ) && (
                <p className="partner-showcase_subtitle">
                  {currentItem?.eyebrowText}
                </p>
              )}

              <div className="partner-showcase__navigation">
                <button
                  className="nav-button"
                  onClick={handlePrevious}
                  aria-label="Previous"
                >
                  
                  {className === "batch-partner-showcase" ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_78_913)">
                        <path d="M9.14286 19.173L10.7143 17.6169L4.2449 11.1266H21V8.87446H4.2449L10.7143 2.40463L9.14286 0.828125L0 10.0005L9.14286 19.173Z" fill="white"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_78_913">
                        <rect width="20" height="20" fill="white" transform="matrix(-1 0 0 1 20 0)"/>
                        </clipPath>
                      </defs>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M15 19l-7-7 7-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>           
                  )}

                </button>

                <button
                  className="nav-button"
                  onClick={handleNext}
                  aria-label="Next"
                > 
                  
                  {className === "batch-partner-showcase" ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_78_916)">
                        <path d="M10.8571 19.173L9.28571 17.6169L15.7551 11.1266H-1V8.87446H15.7551L9.28571 2.40463L10.8571 0.828125L20 10.0005L10.8571 19.173Z" fill="white"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_78_916">
                        <rect width="20" height="20" fill="white"/>
                        </clipPath>
                      </defs>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 5l7 7-7 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>

                <span className="pagination">
                  {currentIndex + 1}/{items.length}
                </span>
              </div>
            </div>
          </div>

          <div className="partner-showcase__image-container">
            {isVideo ? (
              <>
                <div className="partner-showcase__image partner-video">
                  <DynamicMedia
                    src={mediaSrc ?? ""}
                    alt={currentItem?.backgroundImage?.title ?? ""}
                    ariaLabel={currentItem?.backgroundImage?.title ?? ""}
                    videoAutoplay={false}
                    lottieProps={{
                      loop: true,
                      autoplay: false,
                    }}
                    lottieClassName="partner-video-content"
                  />
                </div>
                
                {/* Play/Pause Button for Videos */}
                <button
                  onClick={togglePlayPause}
                  className="video-detail-play-pause-btn"
                >
                  {videoState.showVideo && videoState.isPlaying ? (
                    // Pause icon - when video is playing
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 1V11"
                        stroke="black"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M9 1V11"
                        stroke="black"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    // Play icon - when showing thumbnail or video is paused
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M3 1L10 6L3 11V1Z" fill="black" />
                    </svg>
                  )}
                </button>
              </>
            ) : (
              // Original background image behavior for non-video items
              <div
                className="partner-showcase__image"
                style={{
                  backgroundImage: `url(${currentItem?.backgroundImage?.url})`,
                }}
              />
              
            )}
            {
              !isVideo &&   <div className="partner-showcase__logo">
              <img src={currentItem?.image?.url ?? ""} alt={currentItem?.image?.title ?? ""} />
            </div>
            }
          
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerImageAndList;